import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Outlet, useLoaderData, useLocation, useNavigate, type LoaderFunctionArgs } from 'react-router-dom';
import { Button, cn, Table, TableBody, TableCell, TableHeader, TableRow } from '@immo/ui-kit';
import { ColumnsTrigger, type ColumnPreference } from '@immo/components';
import type { AdminApiKeyListItem, AdminApiKeyStatus, ListAdminApiKeysQuery } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { RequirePermission } from '@/lib/auth/permissions';
import { buildOperatorNameMap, operatorName, type OperatorNameMap } from '@/lib/api-keys/operator-name';
import { useTranslations } from '@/i18n/use-translations';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import {
  ClockIcon,
  KeyIcon,
  MailIcon,
  PlusIcon,
  ShieldIcon,
  TagIcon,
  TextIcon,
  WorkspacesIcon,
} from '@/components/layout/icons';
import { FilterBar, FilterPill } from '@/components/filter-bar';
import { CommitOnBlurInput } from '@/components/commit-on-blur-input';
import { ListEmptyState, TestWorkspacesPill, WorkspaceLabel } from '@/components/test-workspaces';
import { readEnumParam, readUuidParam, useListParams } from '@/lib/list-view/use-list-params';
import { OperatorColumnHead } from '@/components/table/operator-column-head';
import type { OperatorColumn } from '@/components/table/operator-column';
import { loadOperatorColumnPreference, OPERATOR_COLUMN_KEYS } from '@/components/table/operator-column-store';
import { useOperatorTable } from '@/components/table/use-operator-table';
import { includeTestWorkspacesQuery } from '@/lib/test-workspaces/include-test-workspaces';

const STATUSES: ReadonlyArray<AdminApiKeyStatus> = ['active', 'revoked'];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type LoaderData = {
  readonly keys: ReadonlyArray<AdminApiKeyListItem>;
  // id → operator name, resolved client-side from the admin list (issuedBy is a
  // bare admin id — no server-side name join).
  readonly operatorNames: OperatorNameMap;
  readonly columnPreference: ColumnPreference;
};

// The structured filters, read from the URL search params so the loader and any
// shareable/deep links agree. The read side validates, so a hand-edited value is
// ignored rather than forwarded to the backend.
// The test-workspace flag is NOT read from the URL: it is the shared,
// browser-persisted filter (IMM-593.2), spread in at the call site from
// `includeTestWorkspacesQuery()`.
type ListAdminApiKeysFilters = Omit<ListAdminApiKeysQuery, 'includeTestWorkspaces'>;

function queryFromParams(searchParams: URLSearchParams): ListAdminApiKeysFilters {
  const query: ListAdminApiKeysFilters = {};
  const workspaceId = readUuidParam(searchParams, 'workspaceId');
  const status = readEnumParam(searchParams, 'status', STATUSES);

  if (workspaceId !== null) query.workspaceId = workspaceId;

  if (status !== null) query.status = status;

  return query;
}

export const apiKeysLoaderParams = ['workspaceId', 'status'] as const;

export async function apiKeysLoader({ request }: LoaderFunctionArgs): Promise<LoaderData> {
  // Workspace keys only (IMM-587.6). A admin API key is bound to an operator, not
  // a workspace, so it is read on that operator at `/admins/:adminId/api-keys`.
  // Pinned in the loader rather than offered as a filter: the two kinds are
  // managed in different places now, and a filter would imply otherwise.
  const query: ListAdminApiKeysFilters = { ...queryFromParams(new URL(request.url).searchParams), kind: 'workspace' };

  const [result, adminsResult, columnPreference] = await Promise.all([
    apiClient.adminApiKeys.list({ query: { ...query, ...includeTestWorkspacesQuery() } }),
    // The admin list resolves the issuedBy id to a name; a rejection degrades to
    // the generic operator label rather than failing the page.
    apiClient.adminAdmins.list().catch(() => null),
    loadOperatorColumnPreference(OPERATOR_COLUMN_KEYS.apiKeys),
  ]);

  if (!result.ok) {
    throw new Response('Failed to load API keys', { status: result.status });
  }

  return {
    keys: result.body.data,
    operatorNames: buildOperatorNameMap(adminsResult?.ok ? adminsResult.body.data : []),
    columnPreference,
  };
}

// `id` is nullable since IMM-556.1: an admin API key is bound to an operator, so it
// carries neither a workspace nor a member and both columns fall through to the
// fallback. Rendering the row rather than filtering admin API keys out is
// deliberate — a key issued through the API that showed up nowhere would read as
// a failed issuance. IMM-556.3 replaces these two columns with the key's kind
// and scope. `null` where the cell shows `scope.none`; the sort groups it last.
function labelFor(name: string | null, id: string | null): string | null {
  if (name !== null && name !== '') return name;

  if (id !== null && id !== '') return id;

  return null;
}

// The five width-less columns grow with their data: the icon heads overflow `main` by 392px at
// 1280px and still by 232px at 1440px, and only fit from ~1680px with a 24-character e-mail (0 at
// 1280px icon-only), so labels show from 1920px and the head's `title` carries them below. Written
// as a whole literal so Tailwind emits it (BL-043, BL-065; IMM-1074.5 plan).
const LABEL_FROM_1920 = 'hidden min-[1920px]:inline';

function useApiKeyColumns(operatorNames: OperatorNameMap): ReadonlyArray<OperatorColumn<AdminApiKeyListItem>> {
  const t = useTranslations('pages.admin.api-keys');
  const intl = useIntl();

  return useMemo(() => {
    // A admin API key belongs to no workspace and no member (IMM-556.3): its reach
    // is its scope, so the two columns read "—" rather than a blank cell or a
    // misleading person. The bound operator is provenance, not tenancy, and
    // already appears in the "issued by" column.
    const scopeLabel = (key: AdminApiKeyListItem): string => {
      if (key.scope === null) return t('scope.none');

      return key.scope.kind === 'ALL' ? t('scope.all') : t('scope.selected', { count: key.scope.workspaceIds.length });
    };

    const issuedByLabel = (key: AdminApiKeyListItem): string =>
      key.issuedBy === null ? t('customer-created') : operatorName(key.issuedBy, operatorNames, t('operator-fallback'));

    return [
      {
        id: 'name',
        label: t('columns.name'),
        icon: TextIcon,
        headLabelClassName: LABEL_FROM_1920,
        cell: (key) => key.name,
        sortValue: (key) => key.name,
      },
      {
        id: 'workspace',
        label: t('columns.workspace'),
        icon: WorkspacesIcon,
        headLabelClassName: LABEL_FROM_1920,
        cell: (key) => (
          <WorkspaceLabel isTest={key.isTest}>
            {labelFor(key.workspaceName, key.workspaceId) ?? t('scope.none')}
          </WorkspaceLabel>
        ),
        sortValue: (key) => labelFor(key.workspaceName, key.workspaceId),
      },
      {
        id: 'scope',
        label: t('columns.scope'),
        icon: KeyIcon,
        headLabelClassName: LABEL_FROM_1920,
        cell: scopeLabel,
        sortValue: scopeLabel,
      },
      {
        id: 'member',
        label: t('columns.member'),
        icon: MailIcon,
        headLabelClassName: LABEL_FROM_1920,
        cell: (key) => labelFor(key.memberEmail, key.memberId) ?? t('scope.none'),
        sortValue: (key) => labelFor(key.memberEmail, key.memberId),
      },
      {
        id: 'issuedBy',
        label: t('columns.issued-by'),
        icon: ShieldIcon,
        headLabelClassName: LABEL_FROM_1920,
        cell: issuedByLabel,
        sortValue: issuedByLabel,
      },
      {
        id: 'status',
        label: t('columns.status'),
        icon: TagIcon,
        headClassName: 'w-[110px]',
        headLabelClassName: LABEL_FROM_1920,
        cell: (key) => t(`status.${key.status}`),
        sortValue: (key) => t(`status.${key.status}`),
      },
      {
        id: 'rateLimit',
        label: t('columns.rate-limit'),
        icon: TagIcon,
        headClassName: 'w-[110px]',
        headLabelClassName: LABEL_FROM_1920,
        cellClassName: 'tabular-nums',
        cell: (key) => key.rateLimit,
        sortValue: (key) => key.rateLimit,
      },
      {
        id: 'lastUsed',
        label: t('columns.last-used'),
        icon: ClockIcon,
        headClassName: 'w-[170px]',
        headLabelClassName: LABEL_FROM_1920,
        cellClassName: 'text-text-tertiary tabular-nums',
        cell: (key) =>
          key.lastUsedAt === null
            ? t('never')
            : intl.formatDate(key.lastUsedAt, { dateStyle: 'short', timeStyle: 'short' }),
        sortValue: (key) => (key.lastUsedAt === null ? null : Date.parse(key.lastUsedAt)),
      },
    ];
  }, [t, intl, operatorNames]);
}

export function ApiKeysPage() {
  const t = useTranslations('pages.admin.api-keys');
  const tFilters = useTranslations('pages.admin.shell.filters');
  const navigate = useNavigate();
  const location = useLocation();
  const { keys, operatorNames, columnPreference } = useLoaderData() as LoaderData;
  const { params, setParams } = useListParams();
  const catalogue = useApiKeyColumns(operatorNames);
  const table = useOperatorTable({
    rows: keys,
    preferenceKey: OPERATOR_COLUMN_KEYS.apiKeys,
    catalogue,
    alwaysVisibleId: 'name',
    loadedPreference: columnPreference,
  });

  const selectedStatus = readEnumParam(params, 'status', STATUSES);

  function toggleStatus(status: AdminApiKeyStatus): void {
    setParams({ status: selectedStatus === status ? null : status });
  }

  // The workspace-id filter is committed on blur / Enter and only when
  // well-formed — an invalid value clears the param rather than 400-ing the
  // loader.
  function commitUuid(value: string): void {
    const trimmed = value.trim();
    setParams({ workspaceId: trimmed !== '' && UUID_RE.test(trimmed) ? trimmed : null });
  }

  return (
    <>
      <PageTopbar
        leading={<ListLeading title={t('title')} count={keys.length} icon={KeyIcon} />}
        trailing={
          <RequirePermission permission="backoffice:identity:api-key-create">
            <Button
              size="sm"
              onClick={() => navigate({ pathname: '/workspaces/api-keys/new', search: location.search })}
            >
              <PlusIcon className="h-4 w-4" />
              {t('issue.cta')}
            </Button>
          </RequirePermission>
        }
      />
      <FilterBar
        pills={[
          ...STATUSES.map((status) => (
            <FilterPill
              key={`status-${status}`}
              icon={TagIcon}
              label={t(`status.${status}`)}
              active={selectedStatus === status}
              onToggle={() => toggleStatus(status)}
            />
          )),
          <TestWorkspacesPill key="test-workspaces" />,
        ]}
        searchValue=""
        searchPlaceholder={tFilters('search')}
        onSearchChange={() => undefined}
        trailing={<ColumnsTrigger {...table.columns.trigger} />}
      />
      <div className="flex flex-wrap items-end gap-3 border-b border-border bg-surface px-4 py-2">
        <label className="flex flex-col gap-1 text-sm text-text-tertiary">
          {t('filters.workspace-id')}
          <CommitOnBlurInput
            className="w-72"
            value={readUuidParam(params, 'workspaceId') ?? ''}
            placeholder={t('filters.uuid-placeholder')}
            aria-label={t('filters.workspace-id')}
            onCommit={commitUuid}
          />
        </label>
      </div>
      {keys.length === 0 ? (
        <ListEmptyState title={t('empty')} />
      ) : (
        <Table flush>
          <TableHeader>
            <TableRow {...table.columns.headerRowProps}>
              {table.columns.visibleColumns.map((column) => (
                <OperatorColumnHead key={column.id} column={column} table={table} />
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.sorted.map((key) => (
              <TableRow key={key.id} interactive onClick={() => navigate(`/workspaces/api-keys/${key.id}`)}>
                {table.columns.visibleColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={cn(column.cellClassName, table.columns.bodyCellClassName(column.id))}
                    style={column.cellStyle}
                  >
                    {column.cell(key)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Outlet />
    </>
  );
}
